import React, { useState } from 'react'
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Activity,
  RefreshCw,
  Search,
  Download,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Info,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

// Types
interface StagingRow {
  id: number
  status: string | null
  scrapedAt: string
  processedAt?: string
  projectName?: string
  projectWebsiteUrl?: string
  unitRef?: string
  unitTipologia?: string
  unitPiso?: string
  unitFracao?: string
  unitPreco?: number
  unitEstado?: string
  unitUrl?: string
  jobId?: string
  message?: string
}

interface AuditLog {
  id: string
  operation: string
  changedFields?: Record<string, any>
  changeSummary?: string
  source?: string
  timestamp: string
  reason?: string
}

interface PipelineStats {
  total: number
  processed: number
  skipped: number
  error: number
  pending: number
}

const PAGE_SIZE = 20

export function PipelineLog() {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null)
  const [details, setDetails] = useState<Record<number, any>>({})
  const [auditLogs, setAuditLogs] = useState<Record<number, AuditLog[]>>({})
  const [currentPage, setCurrentPage] = useState(1)

  // Fetch pipeline status with filters and pagination
  const { data: pipelineData, isLoading, refetch } = useQuery({
    queryKey: ['pipeline-status', statusFilter, searchTerm, currentPage],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (searchTerm) params.append('search', searchTerm)
      params.append('page', currentPage.toString())
      params.append('limit', PAGE_SIZE.toString())

      const response = await api.get(`/automation/pipeline-status?${params.toString()}`)
      return response.data
    },
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })

  const toggleRowDetails = async (stagingId: number) => {
    if (expandedRowId === stagingId) {
      setExpandedRowId(null)
      return
    }
    try {
      if (!details[stagingId]) {
        const res = await api.get(`/automation/staging/${stagingId}`)
        setDetails(prev => ({ ...prev, [stagingId]: res.data }))
      }
      if (!auditLogs[stagingId]) {
        const auditRes = await api.get(`/automation/staging/${stagingId}/audit-logs`)
        setAuditLogs(prev => ({ ...prev, [stagingId]: auditRes.data }))
      }
      setExpandedRowId(stagingId)
    } catch (e) {
      toast.error('Failed to load details')
    }
  }

  // Use data directly from backend (already filtered and paginated)
  const paginatedRows = pipelineData?.recentActivity || []
  const totalPages = pipelineData?.pagination?.totalPages || 1
  const totalCount = pipelineData?.pagination?.total || 0

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, statusFilter])

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'processed':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'error':
        return <XCircle className="h-4 w-4 text-red-600" />
      case 'skipped':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />
      case 'pending':
        return <Clock className="h-4 w-4 text-blue-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-400" />
    }
  }

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'processed':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200'
      case 'skipped':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'pending':
        return 'text-blue-600 bg-blue-50 border-blue-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const getApifyUrl = (jobId: string | undefined) => {
    if (!jobId) return null
    return `https://console.apify.com/actors/runs/${jobId}#log`
  }

  const formatUnitDisplay = (row: StagingRow) => {
    // Prioritize unitRef, then unitFracao, then fallback
    if (row.unitRef) return row.unitRef
    if (row.unitFracao) return `Fraction: ${row.unitFracao}`
    return 'No unit reference'
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-lg">
            <Activity className="h-6 w-6 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-secondary-900">{pipelineData?.stats?.total || 0}</p>
          <p className="text-sm text-secondary-500">Total Rows</p>
        </div>

        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-green-100 rounded-lg">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-secondary-900">{pipelineData?.stats?.processed || 0}</p>
          <p className="text-sm text-secondary-500">Processed</p>
        </div>

        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-red-100 rounded-lg">
            <XCircle className="h-6 w-6 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-secondary-900">{pipelineData?.stats?.error || 0}</p>
          <p className="text-sm text-secondary-500">Errors</p>
        </div>

        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-yellow-100 rounded-lg">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
          </div>
          <p className="text-2xl font-bold text-secondary-900">{pipelineData?.stats?.skipped || 0}</p>
          <p className="text-sm text-secondary-500">Skipped</p>
        </div>

        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-lg">
            <Clock className="h-6 w-6 text-gray-600" />
          </div>
          <p className="text-2xl font-bold text-secondary-900">{pipelineData?.stats?.pending || 0}</p>
          <p className="text-sm text-secondary-500">Pending</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                className="form-input pl-10"
                placeholder="Search by project name, unit ref, or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="sm:w-48">
            <select
              className="form-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="processed">Processed</option>
              <option value="error">Error</option>
              <option value="skipped">Skipped</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <button
            onClick={() => refetch()}
            className="btn-secondary flex items-center"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Staging Rows Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Summary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Job
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                  Scraped At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {paginatedRows.map((row: StagingRow) => {
                const isError = row.status === 'error'
                const isSkipped = row.status === 'skipped'
                const apifyUrl = getApifyUrl(row.jobId)

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`hover:bg-secondary-50 cursor-pointer transition-colors ${
                        isError ? 'bg-red-50/30' : isSkipped ? 'bg-yellow-50/30' : ''
                      }`}
                      onClick={() => toggleRowDetails(row.id)}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-secondary-900">
                            {formatUnitDisplay(row)}
                          </div>
                          <div className={`text-sm mt-1 ${
                            !row.projectName || row.projectName === 'Unknown Project' || row.projectName.includes('Missing')
                              ? 'text-red-600 font-medium'
                              : 'text-secondary-500'
                          }`}>
                            {row.projectName || 'Missing project name'}
                          </div>
                          {row.unitTipologia && (
                            <div className="text-xs text-secondary-400 mt-1">
                              {row.unitTipologia}
                              {row.unitPiso && ` · Floor ${row.unitPiso}`}
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(row.status)}
                          <span className={`text-sm font-medium px-2 py-1 rounded-full border ${getStatusColor(row.status)}`}>
                            {row.status || 'pending'}
                          </span>
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {row.message ? (
                          <div className={`text-sm ${
                            isError ? 'text-red-700 font-medium' :
                            isSkipped ? 'text-yellow-700 font-medium' :
                            'text-secondary-600'
                          }`}>
                            <div className="max-w-md">
                              {row.message}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-secondary-400">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm text-secondary-900">
                        <div className="space-y-1">
                          <div className="text-secondary-500">Staging ID: <span className="text-secondary-900 font-medium">{row.id}</span></div>
                          {row.unitPreco != null && (
                            <div className="text-secondary-500">
                              Price: <span className="text-secondary-900 font-medium">{Number(row.unitPreco).toLocaleString('pt-PT')} EUR</span>
                            </div>
                          )}
                          {row.unitEstado && (
                            <div className="text-secondary-500">
                              State: <span className="text-secondary-900 font-medium">{row.unitEstado}</span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {apifyUrl ? (
                          <a
                            href={apifyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 font-medium text-sm"
                          >
                            <span className="mr-1">{row.jobId}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-sm text-secondary-400">-</span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm text-secondary-500">
                        <div>
                          {new Date(row.scrapedAt).toLocaleDateString('pt-PT')}
                        </div>
                        <div className="text-xs text-secondary-400">
                          {new Date(row.scrapedAt).toLocaleTimeString('pt-PT')}
                        </div>
                        {row.processedAt && (
                          <div className="text-xs text-secondary-400 mt-1">
                            Processed: {new Date(row.processedAt).toLocaleTimeString('pt-PT')}
                          </div>
                        )}
                      </td>
                    </tr>
                    {expandedRowId === row.id && (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 bg-secondary-50">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="space-y-2">
                              <div className="text-secondary-500 font-medium flex items-center">
                                <Info className="h-4 w-4 mr-1" />
                                Identifiers
                              </div>
                              <div className="bg-white border border-secondary-200 rounded p-3 space-y-1">
                                <div>Staging ID: <span className="text-secondary-900 font-medium">{row.id}</span></div>
                                {details[row.id]?.jobId && (
                                  <div>
                                    Job: <a
                                      href={getApifyUrl(details[row.id].jobId)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary-600 hover:text-primary-800 font-medium inline-flex items-center"
                                    >
                                      {details[row.id].jobId}
                                      <ExternalLink className="h-3 w-3 ml-1" />
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="text-secondary-500 font-medium">Project</div>
                              <div className="bg-white border border-secondary-200 rounded p-3 space-y-1">
                                <div>Name: <span className="text-secondary-900 font-medium">{details[row.id]?.projectName || row.projectName || '-'}</span></div>
                                {details[row.id]?.projectWebsiteUrl && (
                                  <div>
                                    Website: <a
                                      className="text-primary-600 hover:text-primary-800 break-all"
                                      href={details[row.id].projectWebsiteUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {details[row.id].projectWebsiteUrl}
                                    </a>
                                  </div>
                                )}
                                {(details[row.id]?.projectDistrito || details[row.id]?.projectConcelho) && (
                                  <div>Location: <span className="text-secondary-900">{details[row.id]?.projectDistrito || ''} {details[row.id]?.projectConcelho || ''}</span></div>
                                )}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="text-secondary-500 font-medium">Unit</div>
                              <div className="bg-white border border-secondary-200 rounded p-3 space-y-1">
                                <div>Ref: <span className="text-secondary-900 font-medium">{details[row.id]?.unitRef || row.unitRef || '-'}</span></div>
                                {details[row.id]?.unitFracao && (
                                  <div>Fraction: <span className="text-secondary-900 font-medium">{details[row.id].unitFracao}</span></div>
                                )}
                                <div>Status: <span className="text-secondary-900 font-medium">{details[row.id]?.unitEstado || row.unitEstado || '-'}</span></div>
                                {details[row.id]?.unitPreco != null && (
                                  <div>Price: <span className="text-secondary-900 font-medium">{Number(details[row.id].unitPreco).toLocaleString('pt-PT')} EUR</span></div>
                                )}
                                {(details[row.id]?.unitAreaPrivativa || details[row.id]?.unitAreaExterior) && (
                                  <div>Area: <span className="text-secondary-900 font-medium">{details[row.id]?.unitAreaPrivativa || 0} m2 priv · {details[row.id]?.unitAreaExterior || 0} m2 ext</span></div>
                                )}
                                {details[row.id]?.unitUrl && (
                                  <div>
                                    URL: <a
                                      className="text-primary-600 hover:text-primary-800 break-all"
                                      href={details[row.id].unitUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Visit
                                    </a>
                                  </div>
                                )}
                              </div>
                            </div>
                            {details[row.id]?.message && (
                              <div className="md:col-span-3">
                                <div className="text-secondary-500 font-medium mb-2">Message</div>
                                <div className={`whitespace-pre-wrap text-sm border rounded p-3 ${
                                  isError ? 'bg-red-50 border-red-200 text-red-900' :
                                  isSkipped ? 'bg-yellow-50 border-yellow-200 text-yellow-900' :
                                  'bg-white border-secondary-200 text-secondary-900'
                                }`}>
                                  {details[row.id].message}
                                </div>
                              </div>
                            )}
                            <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-secondary-600">
                              <div>Scraped: <span className="text-secondary-900 font-medium">{new Date(details[row.id]?.scrapedAt || row.scrapedAt).toLocaleString('pt-PT')}</span></div>
                              {details[row.id]?.processedAt && (
                                <div>Processed: <span className="text-secondary-900 font-medium">{new Date(details[row.id].processedAt).toLocaleString('pt-PT')}</span></div>
                              )}
                              <div>Status: <span className="text-secondary-900 font-medium">{details[row.id]?.status || row.status || 'pending'}</span></div>
                            </div>

                            {/* Audit Logs Section */}
                            {auditLogs[row.id] && auditLogs[row.id].length > 0 && (
                              <div className="md:col-span-3">
                                <div className="text-secondary-500 font-medium mb-3">Audit Logs</div>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {auditLogs[row.id].map((log) => (
                                    <div key={log.id} className="bg-white border border-secondary-200 rounded p-3 text-xs">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className={`font-medium px-2 py-1 rounded ${
                                          log.operation.includes('ERROR') || log.operation.includes('SCRAP_ERROR')
                                            ? 'bg-red-100 text-red-800'
                                            : log.operation.includes('SKIPPED') || log.operation.includes('WARNING')
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-secondary-100 text-secondary-800'
                                        }`}>
                                          {log.operation}
                                        </span>
                                        <span className="text-secondary-400">{new Date(log.timestamp).toLocaleString('pt-PT')}</span>
                                      </div>
                                      {log.changeSummary && (
                                        <div className="text-secondary-700 mb-2">
                                          <strong>Summary:</strong> {log.changeSummary}
                                        </div>
                                      )}
                                      {log.changedFields && Object.keys(log.changedFields).length > 0 && (
                                        <div className="text-secondary-600 bg-secondary-50 rounded p-2 font-mono text-xs">
                                          <div className="font-medium text-secondary-700 mb-1">Changed Fields:</div>
                                          <pre className="whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                                            {JSON.stringify(log.changedFields, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                      {log.source && (
                                        <div className="mt-2 text-secondary-500">
                                          Source: <span className="text-secondary-700 font-medium">{log.source}</span>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-secondary-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-secondary-700">
                Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm rounded ${
                          currentPage === pageNum
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-secondary-700 border border-secondary-300 hover:bg-secondary-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="btn-secondary flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}

        {paginatedRows.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-secondary-100 rounded-full flex items-center justify-center">
              <Activity className="h-8 w-8 text-secondary-400" />
            </div>
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No staging rows found</h3>
            <p className="text-secondary-500">
              {searchTerm || statusFilter ? 'Try adjusting your filters' : 'No recent scraping activity'}
            </p>
          </div>
        )}
      </div>

      {/* Pipeline Health */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-secondary-900">Pipeline Health</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-secondary-600">Running</span>
            </div>
            <div className="text-sm text-secondary-500">
              Last check: {new Date().toLocaleTimeString('pt-PT')}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {pipelineData?.stats?.processed && pipelineData.stats.total > 0
                ? Math.round((pipelineData.stats.processed / pipelineData.stats.total) * 100)
                : 0}%
            </div>
            <div className="text-sm text-secondary-600">Success Rate</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-red-600 mb-2">
              {pipelineData?.stats?.error || 0}
            </div>
            <div className="text-sm text-secondary-600">Errors (24h)</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {pipelineData?.stats?.pending || 0}
            </div>
            <div className="text-sm text-secondary-600">Pending</div>
          </div>
        </div>
      </div>
    </div>
  )
}
